import { combineReducers } from 'redux';
import {
  GET_HB1416_TRACKER,
  UPDATE_HB1416_TRACKER,
  GENERATE_AEP,
  GENERATE_ALL_AEPS,
  MATCH_TIA_DESIGNATIONS,
  SELECT_HB1416_STUDENT,
} from './hb1416Tracker.actions';
import { ASYNC_ERROR, ASYNC_FINISH, ASYNC_START } from '../../common/index';

const loadingMsg = (state = '', action) => {
  switch (action.type) {
    case ASYNC_START:
      if (action.model === GET_HB1416_TRACKER) {
        return 'Loading HB 1416 Tracker ...';
      }
      return state;
    case ASYNC_ERROR:
    case ASYNC_FINISH:
      if (action.model === GET_HB1416_TRACKER) {
        return '';
      }
      return state;
    default:
      return state;
  }
};

// Worklist is keyed by firebaseId. Each entry carries { student, tracker, margin }
// exactly as the getHb1416Tracker callable returns it (tracker = { requiredHours,
// completedHours, ixlMinutes, sessions, aep, waiverOnFile }).
const worklist = (state = {}, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_HB1416_TRACKER) {
        return action.data.worklist || {};
      }
      if (action.model === UPDATE_HB1416_TRACKER) {
        const { firebaseId, tracker } = action.data;
        if (!firebaseId || !state[firebaseId] || !tracker) return state;
        // The callable returns the RAW tracker node, which never carries the
        // derived fields (requiredHours from identification, defaulted aep) —
        // merge instead of replace so the row keeps its hours targets.
        const existingTracker = state[firebaseId].tracker || {};
        const mergedTracker = Object.assign({}, existingTracker, tracker, {
          requiredHours: existingTracker.requiredHours || {},
          aep: tracker.aep || existingTracker.aep,
          // The raw node omits a cleared exclusion/narrative entirely — read them
          // off the response so clearing takes effect in the row immediately.
          exclusion: tracker.exclusion || null,
          aepNarrative: tracker.aepNarrative || '',
        });
        return Object.assign({}, state, {
          [firebaseId]: Object.assign({}, state[firebaseId], {
            tracker: mergedTracker,
            excluded: mergedTracker.exclusion,
          }),
        });
      }
      if (action.model === GENERATE_AEP) {
        const { firebaseId, language } = action.data;
        if (!firebaseId || !state[firebaseId]) return state;
        const existing = state[firebaseId];
        const existingTracker = existing.tracker || {};
        return Object.assign({}, state, {
          [firebaseId]: Object.assign({}, existing, {
            tracker: Object.assign({}, existingTracker, {
              aep: Object.assign({}, existingTracker.aep, {
                status: 'generated',
                language,
                generatedAt: new Date().toISOString(),
              }),
            }),
          }),
        });
      }
      return state;
    default:
      return state;
  }
};

const selectedStudentId = (state = null, action) => {
  if (action.type === SELECT_HB1416_STUDENT) {
    return action.firebaseId;
  }
  return state;
};

// Per-student AEP generation state, keyed by firebaseId.
// We track it off the GENERATE_AEP request/finish/error actions (which all carry firebaseId)
// rather than the generic async lifecycle action, which does not.
const aepGenerating = (state = {}, action) => {
  switch (action.type) {
    case GENERATE_AEP:
      return Object.assign({}, state, {
        [action.firebaseId]: { language: action.language, status: 'generating' },
      });
    case ASYNC_FINISH:
      if (action.model === GENERATE_AEP && action.data && action.data.firebaseId) {
        const next = Object.assign({}, state);
        next[action.data.firebaseId] = {
          status: 'generated',
          language: action.data.language,
          pdfUrl: action.data.pdfUrl,
        };
        return next;
      }
      return state;
    case ASYNC_ERROR:
      if (action.model === GENERATE_AEP && action.data && action.data.firebaseId) {
        return Object.assign({}, state, {
          [action.data.firebaseId]: { status: 'error', error: action.desc },
        });
      }
      return state;
    default:
      return state;
  }
};

// Bulk AEP generation lifecycle: { status: 'running'|'done'|'error', language,
// summary?, error? }. The confirm dialog reads this to show progress + results.
const bulkAep = (state = null, action) => {
  switch (action.type) {
    case GENERATE_ALL_AEPS:
      return { status: 'running', language: action.language };
    case ASYNC_FINISH:
      if (action.model === GENERATE_ALL_AEPS) {
        return Object.assign({}, state, { status: 'done', summary: action.data.summary });
      }
      return state;
    case ASYNC_ERROR:
      if (action.model === GENERATE_ALL_AEPS) {
        return Object.assign({}, state, { status: 'error', error: action.desc });
      }
      return state;
    default:
      return state;
  }
};

// TIA registry check lifecycle, same shape as bulkAep.
const tiaCheck = (state = null, action) => {
  switch (action.type) {
    case MATCH_TIA_DESIGNATIONS:
      return { status: 'running' };
    case ASYNC_FINISH:
      if (action.model === MATCH_TIA_DESIGNATIONS) {
        return Object.assign({}, state, { status: 'done', summary: action.data.summary });
      }
      return state;
    case ASYNC_ERROR:
      if (action.model === MATCH_TIA_DESIGNATIONS) {
        return Object.assign({}, state, { status: 'error', error: action.desc });
      }
      return state;
    default:
      return state;
  }
};

export const _reducer = combineReducers({
  loadingMsg,
  worklist,
  selectedStudentId,
  aepGenerating,
  bulkAep,
  tiaCheck,
});

export default 'hb1416Tracker.reducers.js';
